// @ts-nocheck
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { XMarkIcon, SparklesIcon, ArrowUpIcon } from './Icons';
import Spinner from './Spinner';
import { generateStudyAssistant } from '../../services/apiService';
import { APP_MODE } from '../../config';

interface StudyAssistantModalProps {
    onClose: () => void;
    /** S4 — danh sách chủ đề học sinh đang yếu, để mở chat đúng ngữ cảnh ôn tập. */
    weakTopics?: Array<{ topic: string; subject?: string; mastery?: number }>;
    /** Câu hỏi cụ thể từ kết quả bài kiểm tra (vd. "Em làm sai câu này, hãy giải thích lại"). */
    contextHint?: string;
}

const DEFAULT_WELCOME = `Chào em! Mình là Trợ lý học tập AI. Mình sẽ giúp em hiểu bài và tự tư duy, không làm bài hộ em nhé.

Em có thể hỏi mình:
• Giải thích khái niệm, công thức
• Gợi ý cách tiếp cận bài tập
• Đặt câu hỏi để em tự suy nghĩ

Hãy gửi câu hỏi hoặc bài tập em đang gặp khó khăn!`;

const buildWeakTopicWelcome = (weakTopics: Array<{ topic: string; subject?: string; mastery?: number }>): string => {
    const lines = weakTopics.slice(0, 5).map((t, i) => {
        const pct = typeof t.mastery === 'number' ? ` (đang nắm ~${Math.round(t.mastery)}%)` : '';
        const subj = t.subject ? ` · ${t.subject}` : '';
        return `${i + 1}. ${t.topic}${subj}${pct}`;
    }).join('\n');
    return `Chào em! Mình thấy em đang ôn các chủ đề sau:\n\n${lines}\n\n` +
        `Em muốn mình giúp gì trước? Mình có thể:\n` +
        `• Giải thích lại lý thuyết của 1 chủ đề\n` +
        `• Gợi ý cách làm bài tập điển hình\n` +
        `• Tóm tắt sai lầm phổ biến để em tránh\n\n` +
        `Em cứ chọn 1 chủ đề và đặt câu hỏi cho mình nhé!`;
};

const StudyAssistantModal: React.FC<StudyAssistantModalProps> = ({ onClose, weakTopics, contextHint }) => {
    const initialWelcome = useMemo(() => {
        if (weakTopics && weakTopics.length > 0) return buildWeakTopicWelcome(weakTopics);
        return DEFAULT_WELCOME;
    }, [weakTopics]);

    const [messages, setMessages] = useState<{ role: 'user' | 'ai'; text: string }[]>(() => {
        const seed: { role: 'user' | 'ai'; text: string }[] = [{ role: 'ai', text: initialWelcome }];
        if (contextHint) seed.push({ role: 'user', text: contextHint });
        return seed;
    });
    const [inputText, setInputText] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSend = async () => {
        const text = inputText.trim();
        if (!text || isProcessing) return;

        setInputText('');
        setMessages(prev => [...prev, { role: 'user', text }]);
        setIsProcessing(true);

        try {
            const newMessages = [...messages, { role: 'user', text }];
            if (APP_MODE === 'PRODUCTION') {
                const res = await generateStudyAssistant(newMessages);
                if (res.reply) {
                    setMessages(prev => [...prev, { role: 'ai', text: res.reply }]);
                }
            } else {
                await new Promise(r => setTimeout(r, 1200));
                setMessages(prev => [...prev, {
                    role: 'ai',
                    text: 'Em thử xem lại khái niệm trong sách. Em hiểu đề bài yêu cầu gì chưa? Hãy nói cho mình biết em đã nghĩ đến bước nào rồi nhé! (Chế độ dev - kết nối API khi PRODUCTION)'
                }]);
            }
        } catch (err) {
            console.error(err);
            setMessages(prev => [...prev, {
                role: 'ai',
                text: 'Xin lỗi em, mình gặp lỗi kết nối. Em thử lại sau nhé!'
            }]);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="w-full max-w-2xl max-h-[85vh] flex flex-col bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100">
                {/* Header */}
                <div className="flex items-center justify-between p-4 bg-slate-900 text-white border-b border-slate-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-500/20 rounded-xl flex items-center justify-center">
                            <SparklesIcon className="h-5 w-5 text-teal-400" />
                        </div>
                        <div>
                            <h3 className="font-black text-lg">Trợ lý học tập AI</h3>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Hướng dẫn hiểu bài • Không giải hộ</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {weakTopics && weakTopics.length > 0 && (
                    <div className="bg-purple-50 border-b border-purple-100 px-4 py-2.5">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-purple-700">Đang ôn chủ đề yếu</p>
                        <p className="mt-0.5 text-xs text-purple-800">
                            {weakTopics.slice(0, 5).map(t => t.topic).join(' · ')}
                        </p>
                    </div>
                )}

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50 min-h-[300px] max-h-[400px]">
                    {messages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[85%] p-4 rounded-2xl ${
                                m.role === 'user'
                                    ? 'bg-teal-600 text-white rounded-tr-md'
                                    : 'bg-white text-slate-800 border border-slate-100 shadow-sm rounded-tl-md'
                            }`}>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</p>
                            </div>
                        </div>
                    ))}
                    {isProcessing && (
                        <div className="flex justify-start">
                            <div className="bg-white p-4 rounded-2xl rounded-tl-md border border-slate-100 shadow-sm">
                                <div className="flex gap-1">
                                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" />
                                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Input */}
                <div className="p-4 border-t bg-white">
                    <div className="flex gap-2">
                        <textarea
                            value={inputText}
                            onChange={e => setInputText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Nhập câu hỏi hoặc bài tập em cần hỗ trợ..."
                            className="flex-1 px-4 py-3 border-2 border-slate-100 rounded-xl focus:border-teal-500 focus:outline-none resize-none text-sm"
                            rows={2}
                            disabled={isProcessing}
                        />
                        <button
                            onClick={handleSend}
                            disabled={!inputText.trim() || isProcessing}
                            className="px-6 py-3 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-200 disabled:cursor-not-allowed text-white font-black text-sm rounded-xl transition-all flex items-center gap-2"
                        >
                            {isProcessing ? <Spinner size="sm" /> : <ArrowUpIcon className="h-5 w-5" />}
                            Gửi
                        </button>
                    </div>
                    <p className="mt-2 text-[10px] text-slate-400 font-medium">
                        Trợ lý sẽ hướng dẫn em hiểu bài qua câu hỏi gợi mở, không đưa đáp án trực tiếp.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default StudyAssistantModal;
